import React, { useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { Select, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import DynamicConfirmModal from "./DynamicConfirmModal";
import { API } from "aws-amplify";
import { specialDoctorID } from "utils/general";
import { useLocation } from "react-router-dom";

export const DoctorDropdown = ({ doctorList, initialValue, userData }) => {
  const [selectedDoctor, setSelectedDoctor] = useState(initialValue);
  const [tempDoctor, setTempDoctor] = useState(initialValue);
  const [hasSelectionChanged, setHasSelectionChanged] = useState(false); // Track if the selection has changed
  const [loading, setLoading] = useState(false); // Loader state
  const [isModalVisible, setIsModalVisible] = useState(false); // Confirmation modal state
  const [modalContent, setModalContent] = useState({
    content: "",
    action: () => {},
  });
  dayjs.extend(isSameOrAfter);

  /*
    Temporary logic for handling the special doctor (Dr. Boucher) until December 1st:
    1. By default, show all doctors except the one with `specialDoctorID` (Dr. Boucher).
    2. If the query parameter contains `specialDoctorID`, then only show that doctor.
    TODO: Remove this conditional logic after December 1st.
  */
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const doctorID = searchParams.get("dpcDoctorID");
  const today = dayjs().startOf("day");
  const temporaryDoctorList =
    doctorID === specialDoctorID
      ? doctorList.filter((dr) => dr.id === specialDoctorID)
      : doctorList.filter((dr) => dr.id !== specialDoctorID);
  const filteredDoctorList = temporaryDoctorList.filter(
    (doc) => !dayjs(doc.startDate).isSameOrAfter(today)
  );
  const doctorOptions = filteredDoctorList.map((item) => ({
    label: item.name,
    value: item.name,
  }));

  const showModal = (content, action) => {
    setModalContent({ content, action });
    setIsModalVisible(true);
  };

  const handleSelectChange = (value) => {
    if (value !== selectedDoctor) {
      setTempDoctor(value); // Update the temporary doctor selection
      setHasSelectionChanged(true); // Mark that the selection has changed
    } else {
      setTempDoctor(selectedDoctor); // Update the temporary doctor selection
      setHasSelectionChanged(false);
    }
  };

  const handleDoctorChange = async () => {
    setLoading(true); // Start loader
    try {
      const myInit = {
        body: {
          currentDoctor: filteredDoctorList.find(
            (item) => item?.name === selectedDoctor
          ),
          futureDoctor: filteredDoctorList.find(
            (item) => item?.name === tempDoctor
          ),
          userID: userData?.key,
          userType: userData?.role,
        },
      };
      let url = `/superAdmin/changeEpcDoctor`;

      await API.post("bazarhealthREST", url, myInit);

      message.success(`Doctor changed successfully to ${tempDoctor}`);

      setSelectedDoctor(tempDoctor); // Save the selected doctor
    } catch (error) {
      console.error("Error saving:", error);
      message.error("Failed to change doctor. Please try again."); // Handle error notification
      handleCancel();
    } finally {
      setLoading(false); // Stop loader
      setHasSelectionChanged(false); // Reset change tracker
      setIsModalVisible(false); // Close the confirmation modal
    }
  };

  const handleCancel = () => {
    setTempDoctor(selectedDoctor); // Reset to the previously selected doctor
    setHasSelectionChanged(false); // Reset change tracker
    setIsModalVisible(false); // Close the confirmation modal
  };

  const handleAction = (actionType) => {
    const actionConfig = {
      delete: {
        content: "Are you sure you want to delete this item?",
        action: () => {
          // Perform delete action here
          console.log("Item deleted");
        },
      },
      changeDoctor: {
        content: `This action will change the primary care for ${userData?.fullName} from ${selectedDoctor} to ${tempDoctor}`,
        action: handleDoctorChange,
      },
    };

    showModal(
      actionConfig[actionType].content,
      actionConfig[actionType].action
    );
  };

  return (
    <div
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <Select
        value={tempDoctor}
        onChange={handleSelectChange}
        options={doctorOptions}
        style={{ minWidth: 150, marginRight: "50px" }}
      />
      {hasSelectionChanged && ( // Show icons only if selection has changed
        <div
          style={{
            position: "absolute", // Ensure buttons don't take extra space
            right: 0, // Align buttons to the right
            top: "50%", // Center vertically
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CloseOutlined
            onClick={handleCancel}
            style={{ marginRight: 8, cursor: "pointer", color: "red" }}
          />
          <CheckOutlined
            onClick={() => handleAction("changeDoctor")} // Show confirmation modal
            style={{ cursor: "pointer", color: "green" }}
          />
        </div>
      )}

      <DynamicConfirmModal
        visible={isModalVisible}
        onConfirm={modalContent.action}
        onCancel={handleCancel}
        content={modalContent.content}
        loading={loading}
      />
    </div>
  );
};

export default DoctorDropdown;
